import { useQueryClient } from '@tanstack/react-query';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { FieldValues, FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Button, Icon, Table, TableVariant } from '@column/column-ui-kit';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { Loading } from '~/components';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { useGetTransferTemplate, useUpdateTransferTemplate } from '~/hooks/useTransferTemplates';
import { prefetchBankAccountsByIds, prefetchCounterpartiesByIds } from '~/lib/api';
import { useNavigate } from '~/lib/navigation';
import { TransferTemplateType } from '~/repositories/Transfer/TransferTemplateRepository';
import { useNotificationStore } from '~/stores/Notification';
import { getDateTimeString } from '~/util';

import { useACHTransfersReviewTable } from './_utils/ach';
import { useBookTransfersReviewTable } from './_utils/book';
import { useWireTransfersReviewTable } from './_utils/wire';
import { ReviewFormValues } from './types';

interface Params {
  id: string;
}

interface LocationState {
  defaultFormValues?: ReviewFormValues;
}

const Container = styled.form`
  display: flex;
  flex-direction: column;
`;

const Wrapper = styled.div`
  padding: 24px;
  display: grid;
  gap: 24px;
`;

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: space-between;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

export const PageTransferTemplatesReview: FC = () => {
  const { id } = useParams<keyof Params>() as Params;
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const [isSubmitLoading, setIsSubmitLoading] = useState<boolean>(false);
  const [isDone, setIsDone] = useState<boolean>(false);

  const location = useLocation();
  const locationState = location.state as LocationState;
  const { handleFormErrors } = useFormErrorHandler();

  const methods = useForm<ReviewFormValues>({
    defaultValues: locationState?.defaultFormValues ?? {
      achTransfers: [],
      wireTransfers: [],
      bookTransfers: [],
    },
  });
  const { control, handleSubmit, reset, watch } = methods;

  const {
    columns: achColumns,
    columnVisibility: achColumnVisibility,
    data: achData,
    states: achStates,
    handleRow: handleAchRow,
    applyErrorCorrectionsAndUpdateTemplate: applyAchCorrections,
  } = useACHTransfersReviewTable({ control, watch, transferTemplateId: id });
  const {
    columns: wireColumns,
    columnVisibility: wireColumnVisibility,
    data: wireData,
    states: wireStates,
    handleRow: handleWireRow,
    applyErrorCorrectionsAndUpdateTemplate: applyWireCorrections,
  } = useWireTransfersReviewTable({ control, watch, transferTemplateId: id });
  const {
    columns: bookColumns,
    columnVisibility: bookColumnVisibility,
    data: bookData,
    states: bookStates,
    handleRow: handleBookRow,
    applyErrorCorrectionsAndUpdateTemplate: applyBookCorrections,
  } = useBookTransfersReviewTable({ control, watch, transferTemplateId: id });

  const templateType = watch('templateType');
  const queryClient = useQueryClient();
  const [isHydratingTemplate, setIsHydratingTemplate] = useState<boolean>(!!id && !locationState?.defaultFormValues);

  const { isLoading: isLoadingGetTransferTemplate, createRequest: getTransferTemplate } = useGetTransferTemplate({
    onError: (error) => {
      addDangerNotification({ content: error.message });
      navigate(`${ROUTE.TRANSFERS}/templates`);
    },
    shouldNotLoad: !!locationState?.defaultFormValues,
  });

  const { createRequest: updateTransferTemplate } = useUpdateTransferTemplate();

  const handleSendTransfersClick = useCallback(
    async (data: ReviewFormValues) => {
      if (isSubmitLoading) return;

      setIsSubmitLoading(true);
      let successfulTransfers = 0;
      let updatedTransfers: any[] = [];

      const hasErrors = [...achStates, ...wireStates, ...bookStates].some((s) => s.type === 'error');

      if (hasErrors) {
        if (data.templateType === TransferTemplateType.ACH) {
          updatedTransfers = await applyAchCorrections();
        } else if (data.templateType === TransferTemplateType.Wire) {
          updatedTransfers = await applyWireCorrections();
        } else if (data.templateType === TransferTemplateType.Book) {
          updatedTransfers = await applyBookCorrections();
        }
        if (!updatedTransfers.length && updatedTransfers.length !== 0) {
          setIsSubmitLoading(false);
          return;
        }
      }

      if (data.templateType === TransferTemplateType.ACH) {
        await Promise.allSettled(
          achData.map(async (_, i) => {
            const success = await handleAchRow(i);
            if (success) successfulTransfers++;
          })
        );
      } else if (data.templateType === TransferTemplateType.Wire) {
        await Promise.allSettled(
          wireData.map(async (_, i) => {
            const success = await handleWireRow(i);
            if (success) successfulTransfers++;
          })
        );
      } else if (data.templateType === TransferTemplateType.Book) {
        await Promise.allSettled(
          bookData.map(async (_, i) => {
            const success = await handleBookRow(i);
            if (success) successfulTransfers++;
          })
        );
      }

      updateTransferTemplate({
        transferTemplateId: id,
        usageCount: (watch('usageCount') ?? 0) + 1,
        lastUsedAt: getDateTimeString(new Date()),
      }).finally(() => {
        setIsSubmitLoading(false);
        setIsDone(true);

        if (successfulTransfers > 0) {
          addSuccessNotification({
            content: (
              <>
                <strong>{successfulTransfers}</strong> Transfers from <strong>"{data.description}"</strong> successfully
                sent
              </>
            ),
            timeout: 8000,
          });
        }
      });
    },
    [
      id,
      watch,
      isSubmitLoading,
      achData.length,
      wireData.length,
      bookData.length,
      achStates,
      wireStates,
      bookStates,
      handleAchRow,
      handleWireRow,
      handleBookRow,
      applyAchCorrections,
      applyWireCorrections,
      applyBookCorrections,
      updateTransferTemplate,
      addSuccessNotification,
    ]
  );

  const navigation = {
    handleEditClick: useCallback(() => {
      navigate(`${ROUTE.TRANSFERS}/templates/edit/${id}`, { state: { defaultFormValues: watch() } });
    }, [id, navigate, watch]),

    handleCancelClick: useCallback(() => {
      navigate(`${ROUTE.TRANSFERS}/templates`, { state: { defaultFormValues: null } });
    }, [navigate]),

    handleViewTransfersClick: useCallback(() => {
      navigate(ROUTE.TRANSFERS, { state: { defaultFormValues: null } });
    }, [navigate]),
  };

  const onSuccess = useCallback(
    (data: ReviewFormValues) => {
      handleSendTransfersClick(data);
    },
    [handleSendTransfersClick]
  );

  const onError: SubmitHandler<FieldValues> = useCallback(
    (errors) => {
      handleFormErrors({ errors });
    },
    [handleFormErrors]
  );

  useEffect(() => {
    if (id && !locationState?.defaultFormValues) {
      setIsHydratingTemplate(true);
      getTransferTemplate({ transferTemplateId: id })
        .then(async (data) => {
          if (!data) return;

          const counterpartyIds = [...(data.achTransfers ?? []), ...(data.wireTransfers ?? [])]
            .map((t) => t.counterpartyId)
            .filter((cpId): cpId is string => Boolean(cpId));
          const bankAccountIds = [
            ...(data.achTransfers ?? []).map((t) => t.bankAccountId),
            ...(data.wireTransfers ?? []).map((t) => t.bankAccountId),
            ...(data.bookTransfers ?? []).flatMap((t) => [t.senderBankAccountId, t.receiverBankAccountId]),
          ].filter((baId): baId is string => Boolean(baId));
          await Promise.all([
            prefetchCounterpartiesByIds(queryClient, counterpartyIds),
            prefetchBankAccountsByIds(queryClient, bankAccountIds),
          ]);

          const newTransferTemplate = { ...data } as Partial<ReviewFormValues>;
          delete newTransferTemplate.version;

          reset(newTransferTemplate);
        })
        .finally(() => setIsHydratingTemplate(false));
    } else if (locationState?.defaultFormValues) {
      reset(locationState.defaultFormValues);
    }
  }, [id, locationState?.defaultFormValues]);

  const hasErrorStates =
    achStates.some((s) => s.type === 'error') ||
    wireStates.some((s) => s.type === 'error') ||
    bookStates.some((s) => s.type === 'error');

  const renderActionButtons = () => (
    <ButtonGroup>
      <Button
        type="button"
        variant="secondary"
        size="small"
        onClick={navigation.handleEditClick}
        isDisabled={isSubmitLoading || isLoading}
      >
        Edit
      </Button>
      {isDone ? (
        hasErrorStates ? (
          <Button
            type="button"
            icon={<Icon.Send />}
            size="small"
            isLoading={isSubmitLoading}
            isDisabled={isLoading}
            onClick={handleSubmit(onSuccess, onError)}
          >
            Retry
          </Button>
        ) : (
          <Button type="button" size="small" onClick={navigation.handleViewTransfersClick}>
            View Transfers
          </Button>
        )
      ) : (
        <Button
          type="button"
          icon={<Icon.Send />}
          size="small"
          isLoading={isSubmitLoading}
          isDisabled={isLoading}
          onClick={handleSubmit(onSuccess, onError)}
        >
          Send Transfers
        </Button>
      )}
    </ButtonGroup>
  );

  const isLoading = (!!isLoadingGetTransferTemplate || isHydratingTemplate) && !locationState?.defaultFormValues;

  return (
    <FormProvider {...methods}>
      <Container onSubmit={handleSubmit(onSuccess, onError)}>
        <PageActions>
          <Button
            type="button"
            variant="secondary"
            size="small"
            onClick={navigation.handleCancelClick}
            isDisabled={isSubmitLoading}
          >
            Cancel
          </Button>
          {renderActionButtons()}
        </PageActions>

        <Loading isLoading={isLoading}>
          <Wrapper>
            {!isLoading && templateType === TransferTemplateType.ACH && (
              <Table
                columns={achColumns}
                columnVisibility={achColumnVisibility}
                data={achData}
                variant={TableVariant.Grid}
              />
            )}
            {!isLoading && templateType === TransferTemplateType.Wire && (
              <Table
                columns={wireColumns}
                columnVisibility={wireColumnVisibility}
                data={wireData}
                variant={TableVariant.Grid}
              />
            )}
            {!isLoading && templateType === TransferTemplateType.Book && (
              <Table
                columns={bookColumns}
                columnVisibility={bookColumnVisibility}
                data={bookData}
                variant={TableVariant.Grid}
              />
            )}

            <ButtonWrapper>
              <Button
                type="button"
                size="small"
                variant="secondary"
                onClick={navigation.handleCancelClick}
                isDisabled={isSubmitLoading}
              >
                Cancel
              </Button>
              {renderActionButtons()}
            </ButtonWrapper>
          </Wrapper>
        </Loading>
      </Container>
    </FormProvider>
  );
};
